// components/ui/dialog.jsx
import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { XIcon } from "lucide-react"

import { cn } from "../../Lib/Cn"
import { useModalBackButton } from "../../Hooks/useModalBackButton";

// Create a context to pass the preventOutsideClick and forceOpen props
const DialogContext = React.createContext({
  preventOutsideClick: true,
  forceOpen: false,
  mobilePopup: false
});

function Dialog({
  preventOutsideClick = true,
  forceOpen = false,
  mobilePopup = false,
  hidden = false,
  open,
  onOpenChange,
  ...props
}) {
  const handleOpenChange = React.useCallback((newOpen) => {
    if (forceOpen && !newOpen) {
      return;
    }

    if (onOpenChange) {
      onOpenChange(newOpen);
    }
  }, [forceOpen, onOpenChange]);

  useModalBackButton(open, forceOpen ? null : handleOpenChange, 'dialog', hidden);

  return (
    <DialogContext.Provider value={{ preventOutsideClick, forceOpen, mobilePopup }}>
      <DialogPrimitive.Root
        data-slot="dialog"
        open={open}
        onOpenChange={handleOpenChange}
        {...props}
      />
    </DialogContext.Provider>
  );
}

// Rest of your dialog components remain the same...
function DialogTrigger({
  ...props
}) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  ...props
}) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  className,
  ...props
}) {
  const { forceOpen } = React.useContext(DialogContext);

  if (forceOpen) {
    return null;
  }

  return (
    <div>
      <DialogPrimitive.Close
        data-slot="dialog-close"
        className={cn(
          "rounded-lg p-2 transition-colors text-gray-500 hover:bg-gray-100 sm:hover:bg-gray-200",
          className,
          "sm:rounded-lg sm:p-2 sm:transition-colors sm:text-text-color"
        )}
        {...props}
      />
    </div>
  );
}

const SmoothOverlay = React.forwardRef(({ className, ...props }, ref) => {
  return (
    <>
      <style>{`
        @keyframes overlayShow {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes overlayHide {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        
        .overlay-enter {
          animation: overlayShow 400ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        .overlay-exit {
          animation: overlayHide 300ms cubic-bezier(0.16, 1, 0.3, 1);
        }
      `}</style>
      <div
        ref={ref}
        className={cn(
          "fixed inset-0 z-50 bg-black/60 overlay-enter pointer-events-none",
          "data-[state=open]:overlay-enter data-[state=closed]:overlay-exit",
          className
        )}
        {...props}
      />
    </>
  );
});
SmoothOverlay.displayName = "SmoothOverlay";

function DialogOverlay({
  className,
  ...props
}) {
  return (
    <DialogPrimitive.Overlay
      asChild
      data-slot="dialog-overlay"
      {...props}
    >
      <SmoothOverlay className={className} />
    </DialogPrimitive.Overlay>
  );
}

const SmoothContent = React.forwardRef(({ className, children, mobilePopup, ...props }, ref) => {
  const mobilePopupClasses = mobilePopup
    ? "top-[50%] translate-y-[-50%] self-center mx-4 max-w-[calc(100%-2rem)] max-h-[90vh] rounded-lg w-full md:w-auto"
    : "top-0 left-0 h-full w-full";

  return (
    <>
      {!mobilePopup && <style>{`
        @keyframes contentShow {
          from { 
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.8);
          }
          to { 
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
        
        @keyframes contentHide {
          from { 
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
          to { 
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.8);
          }
        }
        
        @keyframes contentShowMobile {
          from { 
            opacity: 0;
            transform: scale(0.8);
          }
          to { 
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes contentHideMobile {
          from { 
            opacity: 1;
            transform: scale(1);
          }
          to { 
            opacity: 0;
            transform: scale(0.8);
          }
        }
        
        .dialog-content {
          animation: contentShowMobile 400ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        .dialog-content[data-state="closed"] {
          animation: contentHideMobile 300ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        div[data-state="open"] .dialog-content {
          animation: contentShowMobile 400ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        div[data-state="closed"] .dialog-content {
          animation: contentHideMobile 300ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        @media (min-width: 640px) {
          .dialog-content {
            animation: contentShow 400ms cubic-bezier(0.16, 1, 0.3, 1);
          }
          
          .dialog-content[data-state="closed"] {
            animation: contentHide 300ms cubic-bezier(0.16, 1, 0.3, 1);
          }
          
          div[data-state="open"] .dialog-content {
            animation: contentShow 400ms cubic-bezier(0.16, 1, 0.3, 1);
          }
          
          div[data-state="closed"] .dialog-content {
            animation: contentHide 300ms cubic-bezier(0.16, 1, 0.3, 1);
          }
        }
      `}</style>}
      <div
        ref={ref}
        className={cn(
          "dialog-content bg-background fixed top-0 left-0 sm:top-[50%] sm:left-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] z-50 grid w-full max-w-full sm:max-w-[calc(100%-2rem)] gap-4 sm:rounded-lg border-0 outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0 overflow-hidden pointer-events-auto",
          "sm:max-w-[700px] p-0 flex flex-col h-full sm:h-auto sm:max-h-[95vh] shadow-lg",
          mobilePopupClasses,
          className
        )}
        {...props}
      >
        {children}
      </div>
    </>
  );
});
SmoothContent.displayName = "SmoothContent";

function DialogContent({
  className,
  overlayClassName,
  children,
  showCloseButton = true,
  ...props
}) {
  const { preventOutsideClick, forceOpen, mobilePopup } = React.useContext(DialogContext);

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        asChild
        data-slot="dialog-content"
        onInteractOutside={(preventOutsideClick || forceOpen) ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={forceOpen ? (e) => e.preventDefault() : undefined}
        onOpenAutoFocus={(e) => e.preventDefault()}
        {...props}
      >
        <SmoothContent className={className} mobilePopup={mobilePopup}>
          {children}
        </SmoothContent>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({
  className,
  ...props
}) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        "flex flex-col gap-2 text-center bg-primary text-primary-foreground",
        // Mobile: White bg, dark text, bottom border. Desktop (sm): Green bg, white text.
        "bg-white text-gray-900  z-10 border-b border-sahi-border-color p-4 relative flex flex-row items-center justify-between flex-shrink-0 rounded-none sm:shadow-none sm:z-auto sm:bg-sahi-primary-color sm:text-white sm:border-b-0",
        className,
        "sm:text-left sm:bg-background sm:text-text-color sm:p-4 sm:pb-0 sm:flex sm:flex-row sm:items-center sm:justify-between sm:flex-shrink-0 sm:rounded-t-lg sm:gap-2"
      )}
      {...props}
    />
  );
}

function DialogFooter({
  className,
  ...props
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end p-4",
        className
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        "text-lg leading-none font-semibold text-primary-foreground",
        // Mobile: Dark text. Desktop (sm): White text.
        "text-gray-900 text-lg font-semibold flex-1 text-left sm:text-text-color sm:pr-4",
        className
      )}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        "text-muted-foreground text-sm",
        className
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}